// ============================================================================
// CANONICAL SCHEMAS - DB-FIRST ARCHITECTURE
//
// This file contains the single source of truth for all Firestore document
// schemas and the normalization functions that guarantee data integrity.
//
// RULE: No raw Firestore data should ever reach the UI. All data must pass
// through these normalization functions.
//
// PATHS:
//   ReferralDoc:    tenants/{tenantId}/referrals/{referralId}
//   MetricEventDoc: tenants/{tenantId}/metrics/{eventId}
//   AuditLogDoc:    tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
// ============================================================================

import { Timestamp } from 'firebase/firestore';
import {
  normalizeFieldEvidenceMap,
  normalizeOptionEvidenceMap,
  normalizeRedFlagEvidenceMap,
  normalizeAttachmentEvidenceMap,
} from './evidenceMap.js';
// S1 — Never-PHI write hardening: value-level PHI redaction for clinical answers.
import { redactBlockingPhiDeep, containsBlockingValuePhiDeep } from '../utils/phiScan.js';

// ============================================================================
// ID GENERATION
// ============================================================================

/**
 * Generate a unique referral ID
 * Format: REF-{timestamp36}-{random4}
 */
export const generateReferralId = () => {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `REF-${ts}-${rand}`;
};

/**
 * Generate a unique metric event ID
 * Format: MTR-{timestamp36}-{random4}
 */
export const generateMetricId = () => {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `MTR-${ts}-${rand}`;
};

/**
 * Generate a unique run ID (used for audit logs)
 * Format: RUN-{timestamp36}-{random4}
 */
export const generateRunId = () => {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `RUN-${ts}-${rand}`;
};

/**
 * Generate a stable human-readable receipt token for EHR notes.
 * Format: RG-{7 base32 chars}  (e.g. "RG-A3K7MNP")
 *
 * INVARIANT: Written once at referral creation. Never updated.
 * Used as the stable identifier in EHR notes so clinical staff can
 * look up a referral without knowing internal Firestore IDs.
 */
export const generateReceiptToken = () => {
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let token = 'RG-';
  for (let i = 0; i < 7; i++) {
    token += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return token;
};

// ============================================================================
// TIMESTAMP HELPERS
// ============================================================================

/**
 * Convert to Firestore Timestamp
 */
export const toTimestamp = (date = new Date()) => {
  return Timestamp.fromDate(date);
};

/**
 * Convert Firestore Timestamp to ISO string for display
 */
export const fromTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate().toISOString();
  }
  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }
  return timestamp;
};

// ============================================================================
// OUTCOME STATUS ENUM
// ============================================================================

export const OutcomeStatus = {
  URGENT: 'urgent',
  ROUTINE: 'routine',
  NOT_YET: 'not_yet',
};

// ============================================================================
// OVERRIDE REASON ENUM (Decision Override - Secondary Escape Hatch)
// ============================================================================

/**
 * OVERRIDE_REASONS - Allowed override reason codes
 *
 * When a GP clicks "Override gate" on the Decision screen, they must select
 * one of these reasons to justify bypassing the pathway requirements.
 */
export const OVERRIDE_REASONS = {
  CLINICAL_URGENCY_NOT_CAPTURED: 'clinical_urgency_not_captured',
  DATA_UNAVAILABLE_TODAY: 'data_unavailable_today',
  SPECIALIST_REQUESTED_ANYWAY: 'specialist_requested_anyway',
  SYSTEM_ERROR_PACK_MISMATCH: 'system_error_pack_mismatch',
  OTHER: 'other',
};

/**
 * OVERRIDE_REASON_LABELS - Human-readable labels for override reasons
 */
export const OVERRIDE_REASON_LABELS = {
  [OVERRIDE_REASONS.CLINICAL_URGENCY_NOT_CAPTURED]: 'Clinical urgency not captured by pathway',
  [OVERRIDE_REASONS.DATA_UNAVAILABLE_TODAY]: 'Required data unavailable today',
  [OVERRIDE_REASONS.SPECIALIST_REQUESTED_ANYWAY]: 'Specialist requested referral anyway',
  [OVERRIDE_REASONS.SYSTEM_ERROR_PACK_MISMATCH]: 'System error / Pack mismatch',
  [OVERRIDE_REASONS.OTHER]: 'Other reason',
};

/**
 * OVERRIDE_NOTE_MAX_LENGTH - Maximum length for override notes (non-PHI)
 */
export const OVERRIDE_NOTE_MAX_LENGTH = 240;

/**
 * validateOverrideReason - Check if an override reason is valid
 *
 * @param {string} reason - Override reason code
 * @returns {boolean} True if valid
 */
export const validateOverrideReason = (reason) => {
  return Object.values(OVERRIDE_REASONS).includes(reason);
};

/**
 * buildOverrideRecord - Build a structured override record
 *
 * @param {Object} params
 * @param {string} params.reason - Override reason code (required)
 * @param {string|null} params.note - Optional note (max 240 chars, non-PHI)
 * @param {string} params.confirmedByUid - User ID who confirmed (required)
 * @param {string} params.confirmedByRole - User role (required)
 * @returns {Object} Override record
 * @throws {Error} If required fields are missing or invalid
 */
export const buildOverrideRecord = ({
  reason,
  note = null,
  confirmedByUid,
  confirmedByRole,
}) => {
  if (!reason) throw new Error('buildOverrideRecord: reason is required');
  if (!validateOverrideReason(reason)) {
    throw new Error(`buildOverrideRecord: invalid reason "${reason}". Must be one of: ${Object.values(OVERRIDE_REASONS).join(', ')}`);
  }
  if (!confirmedByUid) throw new Error('buildOverrideRecord: confirmedByUid is required');
  if (!confirmedByRole) throw new Error('buildOverrideRecord: confirmedByRole is required');

  // Sanitize note: truncate and strip dangerous content
  let sanitizedNote = null;
  if (note && typeof note === 'string') {
    sanitizedNote = note.trim().substring(0, OVERRIDE_NOTE_MAX_LENGTH);
    // Remove anything that looks like PHI patterns (extra safety)
    if (sanitizedNote.length === 0) {
      sanitizedNote = null;
    }
  }

  return {
    used: true,
    reason,
    note: sanitizedNote,
    confirmedByUid,
    confirmedByRole,
    confirmedAt: toTimestamp(),
  };
};

/**
 * Normalize outcome string to canonical format
 * Accepts: "Urgent", "URGENT", "urgent", "Routine", "Not Yet", "not_yet", etc.
 */
export const normalizeOutcomeStatus = (outcome) => {
  if (!outcome) return OutcomeStatus.ROUTINE;

  const lower = String(outcome).toLowerCase().replace(/\s+/g, '_');

  if (lower === 'urgent') return OutcomeStatus.URGENT;
  if (lower === 'routine') return OutcomeStatus.ROUTINE;
  if (lower === 'not_yet' || lower === 'notyet') return OutcomeStatus.NOT_YET;

  // Default fallback
  return OutcomeStatus.ROUTINE;
};

// ============================================================================
// PACK NORMALIZATION (EnginePack)
//
// FIRESTORE PATH: tenants/{tenantId}/specialties/{specialtyId}/packs/{packDocId}
//
// This is the NORMALIZATION FIREWALL - no raw pack data should reach the UI.
// All packs must pass through normalizePackDoc() before use.
// ============================================================================

/**
 * Default patient message templates
 */
const DEFAULT_PATIENT_MESSAGES = {
  urgent: 'Your referral requires urgent attention. The specialist office will contact you within 24-48 hours.',
  routine: 'Your referral has been submitted. Expect contact from the specialist office in 2-4 weeks.',
  not_yet: 'Additional information is needed before your referral can be completed. Please follow up with your doctor.',
};

/**
 * Default escalation reasons
 */
const DEFAULT_ESCALATION_REASONS = [
  'Needs specialist decision',
  'Failed first-line treatment',
  'Red flag concern',
  'Imaging interpretation needed',
  'Complex medication management',
  'Other',
];

/**
 * Normalize raw Firestore pack document to EnginePack format
 *
 * INPUT: Raw Firestore document which may have structure:
 * {
 *   name: string,
 *   pack: {
 *     versionId: string,
 *     fields: Field[],
 *     redFlags?: RedFlag[],
 *     not_yet_next_steps?: NextStep[],
 *     patient_message_templates?: { routine, urgent, not_yet },
 *     telehealth_context_fields_enabled?: boolean,
 *     reason_for_escalation_options?: string[]
 *   }
 * }
 *
 * OUTPUT: Canonical EnginePack with guaranteed structure:
 * {
 *   packId, docId, name, versionId,
 *   fields: Field[],
 *   redFlags: RedFlag[],
 *   notYetNextSteps: NextStep[],
 *   telehealthEnabled: boolean,
 *   escalationOptions: string[],
 *   patientMessageTemplates: { routine, urgent, not_yet }
 * }
 *
 * @param {Object} rawDoc - Raw Firestore document data
 * @param {string} docId - Document ID
 * @param {string} specId - Specialty ID (fallback)
 * @returns {Object|null} Normalized EnginePack or null if invalid
 */
export const normalizePackDoc = (rawDoc, docId = null, specId = null) => {
  if (!rawDoc) {
    console.error('[normalizePackDoc] Raw document is null/undefined');
    return null;
  }

  // Extract pack content from nested structure
  // Firestore may store as { name, pack: { fields, ... } } or flat { fields, ... }
  const packContent = rawDoc.pack || rawDoc;

  // Build metadata with fallbacks
  const packId = rawDoc.packId || packContent.metadata?.id || docId?.split('__')[0] || 'unknown';
  const versionId = rawDoc.versionId || packContent.versionId || packContent.metadata?.version || '1.0.0';
  const specialtyId = rawDoc.specialtyId || specId || packContent.metadata?.specialtyId || null;

  const metadata = {
    id: packId,
    name: rawDoc.name || packContent.metadata?.name || 'Unnamed Pack',
    version: versionId,
    specialtyId: specialtyId,
    specialty: packContent.metadata?.specialty || specialtyId || 'Specialty',
    intent: rawDoc.description || packContent.metadata?.intent || '',
  };

  // ========================================================================
  // NORMALIZE FIELDS - Ensure array with proper structure
  // ========================================================================
  const rawFields = packContent.fields || rawDoc.fields || [];
  const fields = rawFields.map((field, idx) => {
    // Normalize options to array of { value, label } objects
    let options = [];
    if (Array.isArray(field.options)) {
      options = field.options.map(opt => {
        if (typeof opt === 'string') {
          return { value: opt, label: opt };
        }
        return {
          value: opt.value || opt.id || opt,
          label: opt.label || opt.value || opt.id || String(opt),
        };
      });
    }

    const normalizedField = {
      id: field.id || `field_${idx}`,
      label: field.label || field.id || `Field ${idx + 1}`,
      type: field.type || 'text',
      required: Boolean(field.required),
      options,
      placeholder: field.placeholder || '',
      helpText: field.helpText || field.description || '',
      min: field.min,
      max: field.max,
      showIf: field.showIf || null,
      // Additional field metadata
      category: field.category || 'clinical',
      order: field.order || idx,
    };

    // Evidence Assist maps (Phase 2C-1) — Never-PHI. Attached only when present;
    // matcher/Gate phases consume these later. No effect on engine decisions.
    const optionValues = options.map((o) => o.value).filter((v) => typeof v === 'string');
    const fieldEvidence = normalizeFieldEvidenceMap(field.evidenceMap);
    const optionEvidence = normalizeOptionEvidenceMap(field.optionEvidenceMap, optionValues);
    if (fieldEvidence) normalizedField.evidenceMap = fieldEvidence;
    if (optionEvidence) normalizedField.optionEvidenceMap = optionEvidence;

    return normalizedField;
  });

  // ========================================================================
  // NORMALIZE RED FLAGS - Ensure array with proper structure
  // ========================================================================
  const rawRedFlags = packContent.redFlags || rawDoc.redFlags || [];
  const redFlags = rawRedFlags.map((rf, idx) => {
    const normalizedFlag = {
      id: rf.id || `rf_${idx}`,
      label: rf.label || rf.id || `Red Flag ${idx + 1}`,
      description: rf.description || '',
      severity: rf.severity || 'high',
      forceUrgent: rf.forceUrgent !== false, // Default true - red flags force urgent
      order: rf.order || idx,
    };
    // Evidence Assist (Phase 2C-1) — Never-PHI; positive_only, never auto-selects.
    const flagEvidence = normalizeRedFlagEvidenceMap(rf.evidenceMap);
    if (flagEvidence) normalizedFlag.evidenceMap = flagEvidence;
    return normalizedFlag;
  });

  // ========================================================================
  // NORMALIZE REDIRECT FLAGS - Pre-outcome routing check (V2: intra + cross)
  //
  // intraSpecialtyRedirectFlags → redirect to another pack inside same specialty
  // crossSpecialtyRedirectFlags → redirect to a different specialty
  //
  // Evaluation order in Gate engine:
  //   1. intraSpecialtyRedirectFlags (checked first)
  //   2. crossSpecialtyRedirectFlags (checked second)
  //
  // Neither array is ever persisted as finalOutcome or workflow/state.
  //
  // LEGACY MIGRATION:
  //   Old packs may have a single `redirectFlags` array.
  //   target.type === 'pack'       → intraSpecialtyRedirectFlags
  //   target.type === anything else → crossSpecialtyRedirectFlags
  // ========================================================================

  /** Normalize one redirect flag item to canonical shape. */
  const _normRedirect = (rf, idx, forcedType) => {
    const base = {
      id: rf.id || `rdf_${forcedType}_${idx}`,
      label: rf.label || rf.id || `Redirect ${idx + 1}`,
      description: rf.description || '',
      redirectReason: rf.redirectReason || '',
      target: {
        type: forcedType,
        targetId: rf.target?.targetId || '',
        label: rf.target?.label || '',
      },
      order: typeof rf.order === 'number' ? rf.order : idx,
    };
    // Cross-specialty flags may optionally carry an exact target pack reference
    // for deep-link quality (Go to "Specialty - Pack" CTA).
    // Both fields are optional; absent = deterministic fallback at runtime.
    if (forcedType === 'specialty') {
      if (rf.target?.targetPackId)    base.target.targetPackId    = rf.target.targetPackId;
      if (rf.target?.targetPackLabel) base.target.targetPackLabel = rf.target.targetPackLabel;
      // Also support legacy 'packId' alias
      if (!base.target.targetPackId && rf.target?.packId) base.target.targetPackId = rf.target.packId;
    }
    return base;
  };

  const rawIntraRedirects  = packContent.intraSpecialtyRedirectFlags || rawDoc.intraSpecialtyRedirectFlags || [];
  const rawCrossRedirects  = packContent.crossSpecialtyRedirectFlags || rawDoc.crossSpecialtyRedirectFlags || [];
  const hasNewRedirectShape = rawIntraRedirects.length > 0 || rawCrossRedirects.length > 0;

  // Legacy migration: only run if no new-shape arrays are present
  const rawLegacyRedirects = (!hasNewRedirectShape)
    ? (packContent.redirectFlags || rawDoc.redirectFlags || [])
    : [];

  const intraSpecialtyRedirectFlags = hasNewRedirectShape
    ? rawIntraRedirects.map((rf, idx) => _normRedirect(rf, idx, 'pack'))
    : rawLegacyRedirects
        .filter(rf => rf.target?.type === 'pack')
        .map((rf, idx) => _normRedirect(rf, idx, 'pack'));

  const crossSpecialtyRedirectFlags = hasNewRedirectShape
    ? rawCrossRedirects.map((rf, idx) => _normRedirect(rf, idx, 'specialty'))
    : rawLegacyRedirects
        .filter(rf => rf.target?.type !== 'pack')
        .map((rf, idx) => _normRedirect(rf, idx, 'specialty'));

  // ========================================================================
  // NORMALIZE NOT YET NEXT STEPS
  // ========================================================================
  const rawNextSteps = packContent.not_yet_next_steps || packContent.notYetNextSteps ||
                       packContent.notYetSteps || rawDoc.notYetNextSteps || [];
  const notYetNextSteps = rawNextSteps.map((step, idx) => ({
    id: step.id || step.stepId || `step_${idx}`,
    label: step.label || step.description || `Step ${idx + 1}`,
    description: step.description || step.label || '',
    order: step.order || idx,
    linkedFieldIds: step.linkedFieldIds || step.linkedFields || [],
    requiredArtifacts: step.requiredArtifacts || [],
  }));

  // ========================================================================
  // NORMALIZE PATIENT MESSAGE TEMPLATES
  // ========================================================================
  const rawMessages = packContent.patient_message_templates ||
                      packContent.patientMessageTemplates ||
                      packContent.patientMessages ||
                      rawDoc.patientMessages || {};
  const patientMessageTemplates = {
    urgent: rawMessages.urgent || rawMessages.Urgent || DEFAULT_PATIENT_MESSAGES.urgent,
    routine: rawMessages.routine || rawMessages.Routine || DEFAULT_PATIENT_MESSAGES.routine,
    not_yet: rawMessages.not_yet || rawMessages.notYet || rawMessages.NotYet || DEFAULT_PATIENT_MESSAGES.not_yet,
  };

  // ========================================================================
  // NORMALIZE TELEHEALTH CONFIG
  // ========================================================================
  const telehealthEnabled = Boolean(
    packContent.telehealth_context_fields_enabled ??
    packContent.telehealthContextFieldsEnabled ??
    packContent.telehealthEnabled ??
    true // Default enabled
  );

  // ========================================================================
  // NORMALIZE ESCALATION REASONS
  // ========================================================================
  const rawEscalationOptions = packContent.reason_for_escalation_options ||
                               packContent.reasonForEscalationOptions ||
                               packContent.escalationReasons ||
                               rawDoc.escalationReasons || [];
  const escalationOptions = rawEscalationOptions.length > 0
    ? rawEscalationOptions
    : DEFAULT_ESCALATION_REASONS;

  // ========================================================================
  // NORMALIZE ATTACHMENT REQUIREMENTS
  // ========================================================================
  const rawAttachments = packContent.attachmentRequirements || rawDoc.attachmentRequirements || [];
  const attachmentRequirements = rawAttachments.map((att, idx) => {
    const normalizedAttachment = {
      id: att.id || `att_${idx}`,
      label: att.label || '',
      attachmentType: att.attachmentType || '',
      description: att.description || '',
      requiredForIntakeCompleteness: Boolean(att.requiredForIntakeCompleteness ?? att.required ?? false),
    };
    // Evidence Assist (Phase 2C-1) — Never-PHI; documentTypeHints reconciled to canonical enum.
    const attachmentEvidence = normalizeAttachmentEvidenceMap(att.evidenceMap);
    if (attachmentEvidence) normalizedAttachment.evidenceMap = attachmentEvidence;
    return normalizedAttachment;
  });

  // ========================================================================
  // BUILD FINAL NORMALIZED PACK
  // ========================================================================
  const normalizedPack = {
    // Document identifiers
    id: docId || rawDoc.id,
    docId: docId || rawDoc.id,
    packDocId: docId || rawDoc.id,
    packId,
    tenantId: rawDoc.tenantId,
    specialtyId,
    versionId,
    status: rawDoc.status || 'active',

    // Display info
    name: metadata.name,
    description: metadata.intent,

    // Metadata object (for compatibility)
    metadata,

    // ====== CORE ENGINE DATA (guaranteed structure) ======

    // Clinical fields (Screen 4)
    fields,

    // Red flags (Screen 3)
    redFlags,

    // Redirect flags V2 — pre-outcome routing check (never a finalOutcome or workflow state)
    // Intra-specialty: redirect to another pack inside the same specialty (evaluated first)
    intraSpecialtyRedirectFlags,
    // Cross-specialty: redirect to a different specialty entirely (evaluated second)
    crossSpecialtyRedirectFlags,

    // Not Yet next steps (Screen 5)
    notYetNextSteps,

    // Patient messages (Screen 6)
    patientMessageTemplates,

    // Telehealth config (Screen 2)
    telehealthEnabled,

    // Escalation reasons (Screen 2)
    escalationOptions,

    // Attachment requirements (Screen 7)
    attachmentRequirements,

    // Legacy compatibility
    patientMessages: patientMessageTemplates,
    rules: packContent.rules || rawDoc.rules || [],
    referralThresholds: packContent.referralThresholds || rawDoc.referralThresholds || {},

    // Timestamps
    createdAt: rawDoc.createdAt,
    updatedAt: rawDoc.updatedAt,

    // Validation flags
    _normalized: true,
    _normalizedAt: new Date().toISOString(),
  };

  // DEBUG: Log normalized pack
  console.log('[normalizePackDoc] Input:', { rawDoc, docId, specId });
  console.log('[normalizePackDoc] Output:', normalizedPack);

  return normalizedPack;
};

/**
 * Validate a normalized pack is ready for the engine
 * Returns { valid: boolean, errors: string[], warnings: string[] }
 */
export const validateEnginePack = (pack) => {
  const errors = [];
  const warnings = [];

  if (!pack) {
    errors.push('Pack is null or undefined');
    return { valid: false, errors, warnings };
  }

  if (!pack._normalized) {
    errors.push('Pack was not normalized through normalizePackDoc()');
  }

  if (!pack.packId && !pack.id) {
    errors.push('Pack is missing packId');
  }

  if (!pack.specialtyId) {
    errors.push('Pack is missing specialtyId');
  }

  if (!pack.versionId) {
    warnings.push('Pack is missing versionId, using default "1.0.0"');
  }

  if (!Array.isArray(pack.fields)) {
    errors.push('Pack fields is not an array');
  } else if (pack.fields.length === 0) {
    warnings.push('Pack has no clinical fields defined');
  }

  if (!Array.isArray(pack.redFlags)) {
    errors.push('Pack redFlags is not an array');
  }

  if (!Array.isArray(pack.notYetNextSteps)) {
    errors.push('Pack notYetNextSteps is not an array');
  }

  if (!Array.isArray(pack.escalationOptions)) {
    errors.push('Pack escalationOptions is not an array');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
};

// ============================================================================
// REFERRAL DOCUMENT BUILDER (BLOCK 1 - Non-PHI)
// Path: tenants/{tenantId}/referrals/{referralId}
// ============================================================================

/**
 * Build a ReferralDoc for Firestore (BLOCK 1 Schema - Non-PHI)
 * Model A: One referral per submission, runId = referralId
 *
 * @param {Object} params
 * @returns {Object} ReferralDoc ready for Firestore
 * @throws {Error} If required fields are missing
 */
export const buildReferralDoc = ({
  tenantId,
  specialtyId,
  packId,
  versionId,
  createdByUid,
  patient,
  answers,
  outcome,
  missingFieldIds = [],
  triggeredRedFlagIds = [],
  completenessScore = 0,
  referralId = null,
}) => {
  // Validation
  if (!tenantId) throw new Error('buildReferralDoc: tenantId is required');
  if (!specialtyId) throw new Error('buildReferralDoc: specialtyId is required');
  if (!packId) throw new Error('buildReferralDoc: packId is required');
  if (!versionId) throw new Error('buildReferralDoc: versionId is required');
  if (!createdByUid) throw new Error('buildReferralDoc: createdByUid is required');
  if (!patient) throw new Error('buildReferralDoc: patient is required');
  if (!outcome) throw new Error('buildReferralDoc: outcome is required');

  // Generate IDs (Model A: runId = referralId)
  const id = referralId || generateReferralId();
  const runId = id; // Same as referralId in Model A
  const now = toTimestamp();
  // Stable EHR receipt token — written once, never updated.
  const receiptToken = generateReceiptToken();

  // Normalize outcome
  const normalizedStatus = normalizeOutcomeStatus(outcome.status || outcome);

  // Build localId (non-identifying patient reference)
  const localId = patient.reference || patient.localId || generateReferralId();

  // Build telehealthContext if applicable
  let telehealthContext = undefined;
  if (patient.visitType === 'video' || patient.visitType === 'phone' || patient.visitType === 'async') {
    telehealthContext = {
      visitType: patient.visitType === 'video' ? 'Video' : patient.visitType === 'phone' ? 'Phone' : 'Async',
      stateRegion: patient.telehealthState || null,
      reasonForEscalation: patient.reasonForEscalation || null,
    };
  }

  return {
    // Required identifiers
    tenantId,
    specialtyId,
    packId,
    versionId,
    packDocId: `${packId}__${versionId}`,  // Firestore pack document ID (format: packId__versionId)
    referralId: id,
    runId,
    receiptToken, // Stable EHR note token (RG-XXXXXXX) — write once, never update

    // Actor
    createdByUid,

    // Timestamps
    createdAt: now,
    savedAt: now,

    // Patient localId (non-PHI)
    localId,

    // Patient demographics (non-PHI: age range + sex category, no DOB/name/MRN)
    ...(patient.age ? { age: patient.age } : {}),
    ...(patient.sex ? { sex: patient.sex } : {}),

    // Visit type + escalation reason + purpose — stored at top level (non-PHI) so they survive
    // schema round-trips and are reliably recoverable on resume / rework without having
    // to reverse-engineer telehealthContext or guess from its absence.
    ...(patient.visitType           ? { visitType: patient.visitType }                    : {}),
    ...(patient.reasonForEscalation ? { reasonForEscalation: patient.reasonForEscalation } : {}),
    ...(patient.visitPurpose        ? { visitPurpose: patient.visitPurpose }               : {}),

    // Telehealth context (optional, non-PHI)
    ...(telehealthContext ? { telehealthContext } : {}),

    // Clinical data (field answers, structured only, non-PHI)
    answers: answers || {},

    // Arrays (default to empty, never undefined)
    triggeredRedFlagIds: Array.isArray(triggeredRedFlagIds) ? triggeredRedFlagIds : [],
    missingFieldIds: Array.isArray(missingFieldIds) ? missingFieldIds : [],

    // Completeness
    completenessScore: typeof completenessScore === 'number' ? completenessScore : 0,

    // Outcome
    outcome: {
      status: normalizedStatus,
      reason: outcome.reason || outcome.rationale || '',
      // Override tracking on outcome (when override is used)
      ...(outcome.override && outcome.override.used ? {
        isOverride: true,
        overrideByUid: outcome.override.confirmedByUid || null,
        overrideAt: outcome.override.confirmedAt || now,
        overrideReason: outcome.override.reason || null,
      } : {}),
    },

    // Full override record (optional, backward compat)
    ...(outcome.override ? { override: outcome.override } : {}),

    // Not-Yet tracking: set hadNotYet when outcome is not_yet
    ...(normalizedStatus === 'not_yet' ? {
      hadNotYet: true,
      firstNotYetAt: now,
    } : {}),
  };
};

// ============================================================================
// IMPACT SETTINGS DOCUMENT BUILDER (BLOCK 4 - Tenant Impact Configuration)
// Path: tenants/{tenantId}/settings/impact
// ============================================================================

/**
 * Default impact settings (safe defaults if settings doc doesn't exist)
 */
export const DEFAULT_IMPACT_SETTINGS = {
  assumptions: {
    minutesSavedPerPreventedReferral: 45,
    minutesSpentPerIncompleteReferral: 15,
  },
  definitions: {
    completedOutcomeStatuses: ['routine', 'urgent'],
    preventedOutcomeStatuses: ['not_yet'],
  },
  segmentation: {
    enableTelehealthContext: true,
  },
  sla: {
    states: {
      UNCLAIMED_NEW:       { slaHours: 4 },
      NEEDS_INFO:          { slaHours: 48 },
      READY_TO_SCHEDULE:   { slaHours: 24 },
      SCHEDULED:           { slaHours: 168 },
      SEEN:                { slaHours: 72 },
    },
    triage: {
      urgentBusinessHours:      4,
      routineBusinessHours:     48,
      clinicalHoldBusinessHours: 72,
    },
    scheduling: {
      businessHours: 48,
    },
    thresholds: {
      warnPercent: 0.10,
      criticalPercent: 0.25,
      source: 'default',
    },
  },
  businessHours: {
    timezone: 'America/New_York',
    workingDays: [1, 2, 3, 4, 5],
    startHour: 8,
    endHour: 17,
  },
};

/**
 * Build an ImpactSettingsDoc for Firestore (BLOCK 4 Schema)
 *
 * @param {Object} params
 * @returns {Object} ImpactSettingsDoc ready for Firestore
 */
export const buildImpactSettings = ({
  tenantId,
  updatedByUid,
  minutesSavedPerPreventedReferral = 45,
  minutesSpentPerIncompleteReferral = 15,
  completedOutcomeStatuses = ['routine', 'urgent'],
  preventedOutcomeStatuses = ['not_yet'],
  enableTelehealthContext = true,
  sla = null,
  businessHours = null,
}) => {
  if (!tenantId) throw new Error('buildImpactSettings: tenantId is required');
  if (!updatedByUid) throw new Error('buildImpactSettings: updatedByUid is required');

  const now = toTimestamp();
  const defaults = DEFAULT_IMPACT_SETTINGS;

  return {
    tenantId,
    updatedAt: now,
    updatedByUid,

    assumptions: {
      minutesSavedPerPreventedReferral:
        typeof minutesSavedPerPreventedReferral === 'number'
          ? minutesSavedPerPreventedReferral
          : 45,
      minutesSpentPerIncompleteReferral:
        typeof minutesSpentPerIncompleteReferral === 'number'
          ? minutesSpentPerIncompleteReferral
          : 15,
    },

    definitions: {
      completedOutcomeStatuses: Array.isArray(completedOutcomeStatuses)
        ? completedOutcomeStatuses
        : ['routine', 'urgent'],
      preventedOutcomeStatuses: Array.isArray(preventedOutcomeStatuses)
        ? preventedOutcomeStatuses
        : ['not_yet'],
    },

    segmentation: {
      enableTelehealthContext: Boolean(enableTelehealthContext),
    },

    sla: sla && typeof sla === 'object' ? {
      states: sla.states && typeof sla.states === 'object'
        ? { ...defaults.sla.states, ...sla.states }
        : { ...defaults.sla.states },
      triage: sla.triage && typeof sla.triage === 'object'
        ? {
            urgentBusinessHours: typeof sla.triage.urgentBusinessHours === 'number' ? sla.triage.urgentBusinessHours : defaults.sla.triage.urgentBusinessHours,
            routineBusinessHours: typeof sla.triage.routineBusinessHours === 'number' ? sla.triage.routineBusinessHours : defaults.sla.triage.routineBusinessHours,
            clinicalHoldBusinessHours: typeof sla.triage.clinicalHoldBusinessHours === 'number' ? sla.triage.clinicalHoldBusinessHours : defaults.sla.triage.clinicalHoldBusinessHours,
          }
        : { ...defaults.sla.triage },
      scheduling: sla.scheduling && typeof sla.scheduling === 'object'
        ? {
            businessHours: typeof sla.scheduling.businessHours === 'number' ? sla.scheduling.businessHours : defaults.sla.scheduling.businessHours,
          }
        : { ...defaults.sla.scheduling },
      thresholds: sla.thresholds && typeof sla.thresholds === 'object'
        ? {
            warnPercent: typeof sla.thresholds.warnPercent === 'number' ? sla.thresholds.warnPercent : defaults.sla.thresholds.warnPercent,
            criticalPercent: typeof sla.thresholds.criticalPercent === 'number' ? sla.thresholds.criticalPercent : defaults.sla.thresholds.criticalPercent,
            source: sla.thresholds.source || 'tenant',
          }
        : { ...defaults.sla.thresholds },
    } : { ...defaults.sla },

    businessHours: businessHours && typeof businessHours === 'object' ? {
      timezone: typeof businessHours.timezone === 'string' ? businessHours.timezone : defaults.businessHours.timezone,
      workingDays: Array.isArray(businessHours.workingDays) ? businessHours.workingDays : defaults.businessHours.workingDays,
      startHour: typeof businessHours.startHour === 'number' ? businessHours.startHour : defaults.businessHours.startHour,
      endHour: typeof businessHours.endHour === 'number' ? businessHours.endHour : defaults.businessHours.endHour,
    } : { ...defaults.businessHours },
  };
};

/**
 * Validate an ImpactSettingsDoc
 */
export const validateImpactSettings = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.updatedByUid) errors.push('updatedByUid is required');

  if (!doc.assumptions?.minutesSavedPerPreventedReferral) {
    errors.push('assumptions.minutesSavedPerPreventedReferral is required');
  }

  if (!Array.isArray(doc.definitions?.completedOutcomeStatuses)) {
    errors.push('definitions.completedOutcomeStatuses must be an array');
  }

  if (!Array.isArray(doc.definitions?.preventedOutcomeStatuses)) {
    errors.push('definitions.preventedOutcomeStatuses must be an array');
  }

  if (doc.sla) {
    const { thresholds } = doc.sla;
    if (thresholds) {
      if (typeof thresholds.warnPercent === 'number') {
        if (thresholds.warnPercent < 0.05 || thresholds.warnPercent > 0.20) {
          errors.push('Warn threshold must be between 5% and 20%');
        }
      }
      if (typeof thresholds.criticalPercent === 'number') {
        if (thresholds.criticalPercent < 0.15 || thresholds.criticalPercent > 0.40) {
          errors.push("Critical threshold must be between 15% and 40%");
        }
      }
      if (typeof thresholds.warnPercent === 'number' && typeof thresholds.criticalPercent === 'number') {
        if (thresholds.warnPercent >= thresholds.criticalPercent) {
          errors.push('Warn threshold must be lower than Critical threshold');
        }
      }
    }
    if (doc.sla.states) {
      for (const [state, cfg] of Object.entries(doc.sla.states)) {
        if (typeof cfg.slaHours !== 'number' || cfg.slaHours < 1) {
          errors.push(`SLA hours for ${state} must be at least 1`);
        }
      }
    }
  }

  if (doc.businessHours) {
    const bh = doc.businessHours;
    if (!bh.timezone) {
      errors.push('Business hours timezone is required');
    }
    if (!Array.isArray(bh.workingDays) || bh.workingDays.length === 0) {
      errors.push('At least one working day is required');
    }
    if (typeof bh.startHour === 'number' && typeof bh.endHour === 'number') {
      if (bh.startHour < 0 || bh.startHour > 23) {
        errors.push('Start hour must be between 0 and 23');
      }
      if (bh.endHour < 1 || bh.endHour > 24) {
        errors.push('End hour must be between 1 and 24');
      }
      if (bh.startHour >= bh.endHour) {
        errors.push('Start hour must be before end hour');
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// ============================================================================
// METRIC EVENT DOCUMENT BUILDER (BLOCK 1 - Analytics Telemetry)
// Path: tenants/{tenantId}/metrics/{eventId}
// ============================================================================

/**
 * Allowed fields for MetricEventDoc (PHI protection whitelist)
 */
export const METRIC_ALLOWED_FIELDS = [
  'tenantId',
  'specialtyId',
  'packId',
  'versionId',
  'referralId',
  'runId',
  'eventId',
  'createdByUid',
  'savedAt',
  'outcomeStatus',
  'completenessScore',
  'missingFieldIds',
  'triggeredRedFlagIds',
  'pdfGenerated',
  'pdfGeneratedAt',
  'overrideUsed',
  'overrideReasonCode',
  'override', // Full override object: { used, reason, note, confirmedByUid, confirmedByRole, confirmedAt }
  'telehealthContext',
  // BLOCK 2: Attempt tracking
  'attempt',
  'prevOutcomeStatus',
  'severitySignals',
  // BLOCK 4: Computed metrics
  'computed',
];

/**
 * Build a MetricEventDoc for Firestore (BLOCK 1 Schema + BLOCK 4 Computed Fields)
 *
 * @param {Object} params
 * @param {Object} params.impactSettings - Optional tenant impact settings (uses defaults if not provided)
 * @returns {Object} MetricEventDoc ready for Firestore
 * @throws {Error} If required fields are missing
 */
export const buildMetricEvent = ({
  tenantId,
  specialtyId,
  packId,
  versionId,
  referralId,
  runId,
  createdByUid,
  outcomeStatus,
  completenessScore = 0,
  missingFieldIds = [],
  triggeredRedFlagIds = [],
  telehealthType = null,
  telehealthState = null,
  reasonForEscalation = null,
  pdfGenerated = false,
  overrideUsed = false,
  overrideReasonCode = null,
  override = null, // Full override object: { used, reason, note, confirmedByUid, confirmedByRole, confirmedAt }
  attempt = 1,
  prevOutcomeStatus = null,
  severitySignals = null,
  eventId = null,
  impactSettings = null, // BLOCK 4: Optional impact settings
}) => {
  // Validation
  if (!tenantId) throw new Error('buildMetricEvent: tenantId is required');
  if (!specialtyId) throw new Error('buildMetricEvent: specialtyId is required');
  if (!packId) throw new Error('buildMetricEvent: packId is required');
  if (!versionId) throw new Error('buildMetricEvent: versionId is required');
  if (!referralId) throw new Error('buildMetricEvent: referralId is required');
  if (!runId) throw new Error('buildMetricEvent: runId is required');
  if (!createdByUid) throw new Error('buildMetricEvent: createdByUid is required');
  if (!outcomeStatus) throw new Error('buildMetricEvent: outcomeStatus is required');

  const id = eventId || generateMetricId();
  const now = toTimestamp();

  // Normalize outcome
  const normalizedOutcome = normalizeOutcomeStatus(outcomeStatus);

  // Build telehealthContext if applicable
  let telehealthContext = undefined;
  if (telehealthType || telehealthState || reasonForEscalation) {
    telehealthContext = {
      ...(telehealthType ? { visitType: telehealthType === 'video' ? 'Video' : telehealthType === 'phone' ? 'Phone' : 'Async' } : {}),
      ...(telehealthState ? { stateRegion: telehealthState } : {}),
      ...(reasonForEscalation ? { reasonForEscalation } : {}),
    };
  }

  // BLOCK 4: Compute derived metrics using settings (or defaults)
  const settings = impactSettings || DEFAULT_IMPACT_SETTINGS;
  const completedStatuses = settings.definitions?.completedOutcomeStatuses || DEFAULT_IMPACT_SETTINGS.definitions.completedOutcomeStatuses;
  const preventedStatuses = settings.definitions?.preventedOutcomeStatuses || DEFAULT_IMPACT_SETTINGS.definitions.preventedOutcomeStatuses;
  const minutesSaved = settings.assumptions?.minutesSavedPerPreventedReferral || DEFAULT_IMPACT_SETTINGS.assumptions.minutesSavedPerPreventedReferral;

  const isCompleted = completedStatuses.includes(normalizedOutcome);
  const isPrevented = preventedStatuses.includes(normalizedOutcome);
  const estimatedMinutesReclaimed = isPrevented ? minutesSaved : 0;

  // BLOCK 5: Conversion tracking - detect Not-Yet → Completed transitions
  const isNotYet = normalizedOutcome === 'not_yet';
  const prevWasNotYet = prevOutcomeStatus ? normalizeOutcomeStatus(prevOutcomeStatus) === 'not_yet' : false;
  const isConversionEvent = prevWasNotYet && isCompleted;

  const computed = {
    isCompleted,
    isPrevented,
    estimatedMinutesReclaimed,
    // BLOCK 5: Conversion flags
    isNotYet,
    isConversionEvent,
  };

  return {
    // Required identifiers
    tenantId,
    specialtyId,
    packId,
    versionId,
    referralId,
    runId,
    eventId: id,

    // Actor
    createdByUid,

    // Timestamp
    savedAt: now,

    // Outcome
    outcomeStatus: normalizedOutcome,
    completenessScore: typeof completenessScore === 'number' ? completenessScore : 0,

    // Arrays (default to empty, never undefined)
    missingFieldIds: Array.isArray(missingFieldIds) ? missingFieldIds : [],
    triggeredRedFlagIds: Array.isArray(triggeredRedFlagIds) ? triggeredRedFlagIds : [],

    // Tracking flags (default false)
    pdfGenerated: Boolean(pdfGenerated),
    overrideUsed: Boolean(overrideUsed || (override && override.used)),
    ...(overrideReasonCode ? { overrideReasonCode } : {}),

    // Full override record (if present)
    ...(override && override.used ? {
      override: {
        used: true,
        reason: override.reason || null,
        note: override.note || null,
        confirmedByUid: override.confirmedByUid || null,
        confirmedByRole: override.confirmedByRole || null,
        confirmedAt: override.confirmedAt || null,
      }
    } : {}),

    // BLOCK 2: Attempt tracking (REQUIRED)
    attempt: typeof attempt === 'number' ? attempt : 1,
    ...(prevOutcomeStatus ? { prevOutcomeStatus: normalizeOutcomeStatus(prevOutcomeStatus) } : {}),

    // BLOCK 2: Severity signals (optional but recommended)
    ...(severitySignals && typeof severitySignals === 'object' ? { severitySignals } : {}),

    // Optional telehealth context (non-PHI)
    ...(telehealthContext && Object.keys(telehealthContext).length > 0 ? { telehealthContext } : {}),

    // BLOCK 4: Computed metrics (REQUIRED)
    computed,
  };
};

// ============================================================================
// AUDIT LOG DOCUMENT BUILDER (BLOCK 11 - Trace Minimization)
// Path: tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
// ============================================================================

// BLOCK 11: Size guardrails for audit traces
const MAX_TRACE_STEPS = 80;
const MAX_DETAIL_CHARS = 200;
const MAX_ARRAY_IDS = 50;
const MAX_STEP_ID_CHARS = 50;
const MAX_RULE_ID_CHARS = 50;

/**
 * Build minimal trace with size guardrails (BLOCK 11)
 *
 * Enforces:
 * - Max trace steps
 * - Max detail string length
 * - Max array sizes (missingFieldIds, triggeredRedFlagIds)
 * - No blob logging (no raw answers, pack dumps, UI state)
 * - Only ids and structured data
 *
 * @param {Array} rawTrace - Raw trace from engine
 * @returns {Object} { trace, traceCount, traceTruncated, traceTruncateReason }
 */
const buildMinimalTrace = (rawTrace) => {
  if (!Array.isArray(rawTrace)) {
    return {
      trace: [],
      traceCount: 0,
      traceTruncated: false,
      traceTruncateReason: null,
    };
  }

  let traceTruncated = false;
  let traceTruncateReason = null;

  // Limit trace steps
  let steps = rawTrace;
  if (steps.length > MAX_TRACE_STEPS) {
    steps = steps.slice(0, MAX_TRACE_STEPS);
    traceTruncated = true;
    traceTruncateReason = 'max_steps';
  }

  // Sanitize and minimize each step
  const trace = steps.map((step, idx) => {
    // Extract only safe, minimal fields
    const stepId = String(step.stepId || step.t || `step_${idx}`).substring(0, MAX_STEP_ID_CHARS);
    const ruleId = step.ruleId || step.rule || step.t || null;
    const kind = step.kind || 'system';
    const result = step.result || 'pass';

    // Truncate detail string
    let detail = step.detail || step.message || null;
    if (detail && typeof detail === 'string' && detail.length > MAX_DETAIL_CHARS) {
      detail = detail.substring(0, MAX_DETAIL_CHARS) + '...';
      traceTruncated = true;
      if (!traceTruncateReason) traceTruncateReason = 'max_bytes';
    }

    // Build minimal data object (ids only, no blobs)
    const data = {};
    if (step.data) {
      // Only allow safe, id-based fields
      if (step.data.fieldId) data.fieldId = String(step.data.fieldId).substring(0, MAX_STEP_ID_CHARS);
      if (step.data.redFlagId) data.redFlagId = String(step.data.redFlagId).substring(0, MAX_STEP_ID_CHARS);

      // Array ids - truncate to max length
      if (Array.isArray(step.data.missingFieldIds)) {
        data.missingFieldIds = step.data.missingFieldIds
          .slice(0, MAX_ARRAY_IDS)
          .map(id => String(id).substring(0, MAX_STEP_ID_CHARS));
        if (step.data.missingFieldIds.length > MAX_ARRAY_IDS) {
          traceTruncated = true;
          if (!traceTruncateReason) traceTruncateReason = 'max_bytes';
        }
      }

      if (Array.isArray(step.data.triggeredRedFlagIds)) {
        data.triggeredRedFlagIds = step.data.triggeredRedFlagIds
          .slice(0, MAX_ARRAY_IDS)
          .map(id => String(id).substring(0, MAX_STEP_ID_CHARS));
        if (step.data.triggeredRedFlagIds.length > MAX_ARRAY_IDS) {
          traceTruncated = true;
          if (!traceTruncateReason) traceTruncateReason = 'max_bytes';
        }
      }

      if (typeof step.data.severity === 'number') {
        data.severity = step.data.severity;
      }
    }

    // Timestamp
    const t = step.t || step.timestamp || toTimestamp();

    return {
      stepId,
      ...(ruleId ? { ruleId: String(ruleId).substring(0, MAX_RULE_ID_CHARS) } : {}),
      kind,
      result,
      ...(detail ? { detail } : {}),
      ...(Object.keys(data).length > 0 ? { data } : {}),
      t,
    };
  });

  return {
    trace,
    traceCount: trace.length,
    traceTruncated,
    traceTruncateReason,
  };
};

/**
 * Build an AuditLogDoc for Firestore (BLOCK 11 - Minimal Trace)
 *
 * @param {Object} params
 * @returns {Object} AuditLogDoc ready for Firestore
 * @throws {Error} If required fields are missing
 */
export const buildAuditLog = ({
  tenantId,
  referralId,
  runId,
  specialtyId,
  packId,
  versionId,
  outcomeStatus,
  trace = [],
  artifacts = [],
  createdByUid,
  createdByName = null,
  createdByEmail = null,
  createdByRole = null,
  engineVersion = '1.0.0',
  durationMs = 0,
  attempt = 1,
}) => {
  // Validation
  if (!tenantId) throw new Error('buildAuditLog: tenantId is required');
  if (!referralId) throw new Error('buildAuditLog: referralId is required');
  if (!runId) throw new Error('buildAuditLog: runId is required');
  if (!specialtyId) throw new Error('buildAuditLog: specialtyId is required');
  if (!packId) throw new Error('buildAuditLog: packId is required');
  if (!versionId) throw new Error('buildAuditLog: versionId is required');
  if (!createdByUid) throw new Error('buildAuditLog: createdByUid is required');

  const now = toTimestamp();

  // BLOCK 11: Build minimal trace with size guardrails
  const { trace: minimalTrace, traceCount, traceTruncated, traceTruncateReason } = buildMinimalTrace(trace);

  // BLOCK 11: Artifacts should be ids/labels only (truncate if necessary)
  const safeArtifacts = Array.isArray(artifacts)
    ? artifacts.slice(0, MAX_ARRAY_IDS).map(a => String(a).substring(0, MAX_DETAIL_CHARS))
    : [];

  // Submitter snapshot: denormalized at write-time so audit log readers
  // never need to resolve user profiles (avoids permission-denied on read).
  // Safe non-PHI fields only; truncated to reasonable lengths.
  const safeName = typeof createdByName === 'string' ? createdByName.substring(0, 120) : null;
  const safeEmail = typeof createdByEmail === 'string' ? createdByEmail.substring(0, 120) : null;
  const safeRole = typeof createdByRole === 'string' ? createdByRole.substring(0, 40) : null;

  return {
    // Required identifiers
    tenantId,
    referralId,
    runId,
    specialtyId,
    packId,
    versionId,

    // Outcome
    outcomeStatus: normalizeOutcomeStatus(outcomeStatus),

    // BLOCK 11: Minimal trace data with size constraints
    trace: minimalTrace,
    artifacts: safeArtifacts,

    // Actor
    createdByUid,
    // Denormalized submitter snapshot (non-PHI)
    ...(safeName && { createdByName: safeName }),
    ...(safeEmail && { createdByEmail: safeEmail }),
    ...(safeRole && { createdByRole: safeRole }),

    // Engine metadata
    engineVersion,
    durationMs: typeof durationMs === 'number' ? durationMs : 0,

    // BLOCK 2: Attempt tracking
    attempt: typeof attempt === 'number' ? attempt : 1,

    // BLOCK 11: Trace guardrail metadata
    traceCount,
    traceTruncated,
    ...(traceTruncateReason ? { traceTruncateReason } : {}),

    // Timestamp
    createdAt: now,
  };
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate a ReferralDoc has all required fields
 */
export const validateReferralDoc = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.specialtyId) errors.push('specialtyId is required');
  if (!doc.packId) errors.push('packId is required');
  if (!doc.versionId) errors.push('versionId is required');
  if (!doc.createdByUid) errors.push('createdByUid is required');
  if (!doc.runId) errors.push('runId is required');
  if (!doc.localId) errors.push('localId is required');
  if (!doc.outcome?.status) errors.push('outcome.status is required');

  if (!Array.isArray(doc.missingFieldIds)) {
    errors.push('missingFieldIds must be an array');
  }
  if (!Array.isArray(doc.triggeredRedFlagIds)) {
    errors.push('triggeredRedFlagIds must be an array');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Validate a MetricEventDoc has all required fields
 */
export const validateMetricEvent = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.specialtyId) errors.push('specialtyId is required');
  if (!doc.packId) errors.push('packId is required');
  if (!doc.versionId) errors.push('versionId is required');
  if (!doc.referralId) errors.push('referralId is required');
  if (!doc.runId) errors.push('runId is required');
  if (!doc.createdByUid) errors.push('createdByUid is required');
  if (!doc.outcomeStatus) errors.push('outcomeStatus is required');

  if (!Array.isArray(doc.missingFieldIds)) {
    errors.push('missingFieldIds must be an array');
  }
  if (!Array.isArray(doc.triggeredRedFlagIds)) {
    errors.push('triggeredRedFlagIds must be an array');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Validate an AuditLogDoc has all required fields (BLOCK 11 - Enhanced)
 */
export const validateAuditLog = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.referralId) errors.push('referralId is required');
  if (!doc.runId) errors.push('runId is required');
  if (!doc.specialtyId) errors.push('specialtyId is required');
  if (!doc.packId) errors.push('packId is required');
  if (!doc.versionId) errors.push('versionId is required');
  if (!doc.createdByUid) errors.push('createdByUid is required');

  if (!Array.isArray(doc.trace)) {
    errors.push('trace must be an array');
  }
  if (!Array.isArray(doc.artifacts)) {
    errors.push('artifacts must be an array');
  }

  // BLOCK 11: Validate trace size guardrails
  if (typeof doc.traceCount !== 'number') {
    errors.push('traceCount must be a number');
  }
  if (typeof doc.traceTruncated !== 'boolean') {
    errors.push('traceTruncated must be a boolean');
  }

  // BLOCK 11: Validate trace steps structure
  if (Array.isArray(doc.trace)) {
    doc.trace.forEach((step, idx) => {
      if (!step.stepId) {
        errors.push(`trace[${idx}].stepId is required`);
      }
      if (!step.kind) {
        errors.push(`trace[${idx}].kind is required`);
      }
      if (!step.result) {
        errors.push(`trace[${idx}].result is required`);
      }
      if (!step.t) {
        errors.push(`trace[${idx}].t (timestamp) is required`);
      }
    });

    // BLOCK 11: Enforce trace count limit
    if (doc.trace.length > MAX_TRACE_STEPS) {
      errors.push(`trace exceeds maximum allowed steps (${MAX_TRACE_STEPS})`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// ============================================================================
// SUBMISSION LOCK DOCUMENT BUILDER (BLOCK 9 - Idempotency + Reliability)
// Path: tenants/{tenantId}/submissions/{submissionKey}
// ============================================================================

/**
 * Build a SubmissionLockDoc for Firestore (BLOCK 9 Schema)
 *
 * Ensures exactly-once submission via idempotency key
 *
 * @param {Object} params
 * @returns {Object} SubmissionLockDoc ready for Firestore
 */
export const buildSubmissionLock = ({
  tenantId,
  submissionKey,
  referralId,
  runId,
  eventId,
  createdByUid,
  status = 'pending',
  lastError = null,
  attempts = 1,
  expiresAt = null,
}) => {
  if (!tenantId) throw new Error('buildSubmissionLock: tenantId is required');
  if (!submissionKey) throw new Error('buildSubmissionLock: submissionKey is required');
  if (!referralId) throw new Error('buildSubmissionLock: referralId is required');
  if (!runId) throw new Error('buildSubmissionLock: runId is required');
  if (!eventId) throw new Error('buildSubmissionLock: eventId is required');
  if (!createdByUid) throw new Error('buildSubmissionLock: createdByUid is required');

  const now = toTimestamp();

  // Default expiration: 24 hours from now
  const defaultExpiry = new Date();
  defaultExpiry.setHours(defaultExpiry.getHours() + 24);

  return {
    tenantId,
    submissionKey,
    referralId,
    runId,
    eventId,
    uid: createdByUid,  // Firestore rules check resource.data.uid == request.auth.uid
    createdAt: now,
    updatedAt: now,
    status, // "pending" | "committed" | "failed"
    lastError: lastError || null,
    attempts: typeof attempts === 'number' ? attempts : 1,
    expiresAt: expiresAt || Timestamp.fromDate(defaultExpiry),
  };
};

/**
 * Validate a SubmissionLockDoc
 */
export const validateSubmissionLock = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.submissionKey) errors.push('submissionKey is required');
  if (!doc.referralId) errors.push('referralId is required');
  if (!doc.runId) errors.push('runId is required');
  if (!doc.eventId) errors.push('eventId is required');
  if (!doc.uid) errors.push('uid is required');
  if (!['pending', 'committed', 'failed'].includes(doc.status)) {
    errors.push('status must be "pending", "committed", or "failed"');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// ============================================================================
// RUN SESSION DOCUMENT BUILDER (BLOCK 10 - Funnel Telemetry)
// Path: tenants/{tenantId}/runSessions/{runId}
// ============================================================================

/**
 * Generate a run session ID (UUID)
 * @returns {string} UUID
 */
export const generateRunSessionId = () => {
  return `run_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Build a RunSessionDoc for Firestore (BLOCK 10 Schema)
 *
 * Tracks run funnel: started → in_progress → submitted/abandoned
 *
 * @param {Object} params
 * @returns {Object} RunSessionDoc ready for Firestore
 */
export const buildRunSession = ({
  tenantId,
  runId,
  referralId = null,
  specialtyId,
  packId,
  versionId,
  createdByUid,
  state = 'started',
  lastScreen = 1,
  completionPct = 0,
  missingRequiredCount = 0,
  attempt = 1,
}) => {
  if (!tenantId) throw new Error('buildRunSession: tenantId is required');
  if (!runId) throw new Error('buildRunSession: runId is required');
  if (!specialtyId) throw new Error('buildRunSession: specialtyId is required');
  if (!packId) throw new Error('buildRunSession: packId is required');
  if (!versionId) throw new Error('buildRunSession: versionId is required');
  if (!createdByUid) throw new Error('buildRunSession: createdByUid is required');

  const now = toTimestamp();

  return {
    tenantId,
    runId,
    referralId: referralId || null,
    specialtyId,
    packId,
    versionId,
    createdByUid,

    // Funnel timestamps
    startedAt: now,
    lastSeenAt: now,
    submittedAt: null,
    abandonedAt: null,

    // Funnel state
    state, // "started" | "in_progress" | "submitted" | "abandoned"

    // Progress tracking (non-PHI)
    lastScreen: typeof lastScreen === 'number' ? lastScreen : 1,
    completionPct: typeof completionPct === 'number' ? completionPct : 0,
    missingRequiredCount: typeof missingRequiredCount === 'number' ? missingRequiredCount : 0,
    attempt: typeof attempt === 'number' ? attempt : 1,
  };
};

/**
 * Validate a RunSessionDoc
 */
export const validateRunSession = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.runId) errors.push('runId is required');
  if (!doc.specialtyId) errors.push('specialtyId is required');
  if (!doc.packId) errors.push('packId is required');
  if (!doc.versionId) errors.push('versionId is required');
  if (!doc.createdByUid) errors.push('createdByUid is required');
  if (!doc.startedAt) errors.push('startedAt is required');
  if (!doc.lastSeenAt) errors.push('lastSeenAt is required');
  if (!['started', 'in_progress', 'submitted', 'abandoned'].includes(doc.state)) {
    errors.push('state must be "started", "in_progress", "submitted", or "abandoned"');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// ============================================================================
// DAILY ROLLUP DOCUMENT BUILDER (BLOCK 12 - Scalable Platform Analytics)
// Path: tenants/{tenantId}/rollups/daily/{dateId}
// ============================================================================

/**
 * Generate a date ID (YYYY-MM-DD) from a timestamp or date
 * Uses UTC to ensure consistent date keys across timezones
 *
 * @param {Timestamp|Date|null} ts - Timestamp or Date to convert
 * @returns {string} Date ID in YYYY-MM-DD format
 */
export const generateDateId = (ts = null) => {
  const date = ts ? (ts.toDate ? ts.toDate() : new Date(ts)) : new Date();
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Build a DailyRollupDoc for Firestore (BLOCK 12 Schema)
 *
 * Pre-aggregated daily metrics per tenant for fast platform analytics
 * Avoids scanning all metrics docs for dashboard queries
 *
 * Incrementally updated via FieldValue.increment() when metrics are written
 *
 * @param {Object} params
 * @returns {Object} DailyRollupDoc ready for Firestore
 */
export const buildDailyRollup = ({
  tenantId,
  dateId,
  counts = {
    totalRuns: 0,
    routine: 0,
    urgent: 0,
    not_yet: 0,
    completed: 0,
    overrides: 0,
    pdfGenerated: 0,
  },
  value = {
    reclaimedMinutes: 0,
  },
  breakdowns = {
    bySpecialty: {},
    byPack: {},
  },
}) => {
  if (!tenantId) throw new Error('buildDailyRollup: tenantId is required');
  if (!dateId) throw new Error('buildDailyRollup: dateId is required (YYYY-MM-DD format)');

  // Validate dateId format (YYYY-MM-DD)
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(dateId)) {
    throw new Error('buildDailyRollup: dateId must be in YYYY-MM-DD format');
  }

  const now = toTimestamp();

  return {
    tenantId,
    dateId,
    updatedAt: now,

    counts: {
      totalRuns: typeof counts.totalRuns === 'number' ? counts.totalRuns : 0,
      routine: typeof counts.routine === 'number' ? counts.routine : 0,
      urgent: typeof counts.urgent === 'number' ? counts.urgent : 0,
      not_yet: typeof counts.not_yet === 'number' ? counts.not_yet : 0,
      completed: typeof counts.completed === 'number' ? counts.completed : 0,
      overrides: typeof counts.overrides === 'number' ? counts.overrides : 0,
      pdfGenerated: typeof counts.pdfGenerated === 'number' ? counts.pdfGenerated : 0,
    },

    value: {
      reclaimedMinutes: typeof value.reclaimedMinutes === 'number' ? value.reclaimedMinutes : 0,
    },

    breakdowns: {
      bySpecialty: breakdowns.bySpecialty || {},
      byPack: breakdowns.byPack || {},
    },
  };
};

/**
 * Validate a DailyRollupDoc
 */
export const validateDailyRollup = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.dateId) errors.push('dateId is required');

  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (doc.dateId && !datePattern.test(doc.dateId)) {
    errors.push('dateId must be in YYYY-MM-DD format');
  }

  if (!doc.counts || typeof doc.counts !== 'object') {
    errors.push('counts object is required');
  } else {
    const requiredCountFields = ['totalRuns', 'routine', 'urgent', 'not_yet', 'completed', 'overrides', 'pdfGenerated'];
    requiredCountFields.forEach(field => {
      if (typeof doc.counts[field] !== 'number') {
        errors.push(`counts.${field} must be a number`);
      }
    });
  }

  if (!doc.value || typeof doc.value !== 'object') {
    errors.push('value object is required');
  } else {
    if (typeof doc.value.reclaimedMinutes !== 'number') {
      errors.push('value.reclaimedMinutes must be a number');
    }
  }

  if (!doc.breakdowns || typeof doc.breakdowns !== 'object') {
    errors.push('breakdowns object is required');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Build increment object for updating DailyRollupDoc (BLOCK 12)
 *
 * Used with Firestore FieldValue.increment() to atomically update counters
 *
 * @param {Object} metricEvent - The metric event being recorded
 * @param {number} minutesSavedPerPreventedReferral - From tenant impact settings
 * @returns {Object} Update object for Firestore set/update with merge
 */
export const buildDailyRollupIncrements = (metricEvent, minutesSavedPerPreventedReferral = DEFAULT_IMPACT_SETTINGS.assumptions.minutesSavedPerPreventedReferral) => {
  if (!metricEvent) throw new Error('buildDailyRollupIncrements: metricEvent is required');
  if (!metricEvent.outcomeStatus) throw new Error('buildDailyRollupIncrements: metricEvent.outcomeStatus is required');

  const outcomeStatus = normalizeOutcomeStatus(metricEvent.outcomeStatus);

  // Base increments
  const increments = {
    'counts.totalRuns': 1,
    [`counts.${outcomeStatus}`]: 1,
  };

  // Increment completed count if routine or urgent
  if (outcomeStatus === 'routine' || outcomeStatus === 'urgent') {
    increments['counts.completed'] = 1;
  }

  // Increment overrides if override was used
  if (metricEvent.overrideUsed === true) {
    increments['counts.overrides'] = 1;
  }

  // Increment pdfGenerated if pdf was generated
  if (metricEvent.pdfGenerated === true) {
    increments['counts.pdfGenerated'] = 1;
  }

  // Calculate reclaimed minutes for not_yet outcomes
  if (outcomeStatus === 'not_yet') {
    increments['value.reclaimedMinutes'] = minutesSavedPerPreventedReferral;
  }

  // Breakdown by specialty (rich gate sub-object)
  if (metricEvent.specialtyId) {
    const sk = `breakdowns.bySpecialty.${metricEvent.specialtyId}`;
    increments[`${sk}.gate.totalRuns`] = 1;
    increments[`${sk}.gate.${outcomeStatus}`] = 1;
    if (outcomeStatus === 'routine' || outcomeStatus === 'urgent') {
      increments[`${sk}.gate.completed`] = 1;
    }
    if (metricEvent.overrideUsed === true) {
      increments[`${sk}.gate.overrides`] = 1;
    }
    if (metricEvent.isFirstRun !== false) {
      increments[`${sk}.gate.created`] = 1;
    }
    if (outcomeStatus === 'not_yet') {
      increments[`${sk}.capacity.reclaimedMinutes`] = minutesSavedPerPreventedReferral;
    }
  }

  // Breakdown by pack
  if (metricEvent.packId) {
    const packKey = `breakdowns.byPack.${metricEvent.packId}`;
    increments[`${packKey}.totalRuns`] = 1;
    increments[`${packKey}.${outcomeStatus}`] = 1;
  }

  return increments;
};

// ============================================================================
// BLOCK 13: PHI POLICY (Non-PHI Enforcement Firewall - Pilot Mode Runtime Safety)
// ============================================================================

/**
 * ALLOWED_FIELD_TYPES - Field types that are safe to persist (non-PHI)
 *
 * These field types produce structured, constrained data that cannot contain
 * patient-identifying information when used properly.
 *
 * NEW SPEC: textarea is ALLOWED but with runtime safety guardrails (scan, attest, warn)
 */
export const ALLOWED_FIELD_TYPES = [
  'select',      // Single selection from predefined options
  'multiselect', // Multiple selections from predefined options
  'checkbox',    // Boolean values only
  'radio',       // Radio button selection from options
  'number',      // Numeric values only
  'date',        // Date picker (constrained format)
  'severity',    // 0-10 severity scale (numeric)
  'slider',      // Numeric slider (e.g. pain scale 0-10) — stores integer, PHI-safe
  'textarea',    // ALLOWED with runtime PHI scanning + attestation
];

/**
 * BLOCKED_FIELD_TYPES - Field types that are truly unsafe (file uploads, images)
 *
 * These field types cannot be made safe even with runtime guardrails
 */
export const BLOCKED_FIELD_TYPES = [
  'file_upload',    // BLOCKED: Cannot scan binary files for PHI
  'image_upload',   // BLOCKED: Cannot scan images for PHI
];

/**
 * PHI_POLICY_VERSION - Current PHI policy version
 */
export const PHI_POLICY_VERSION = 'pilot-v2';

/**
 * DEFAULT_PHI_POLICY - Default PHI enforcement policy (Pilot Mode)
 *
 * NEW SPEC: Pilot mode enabled by default, textarea allowed with runtime safety
 */
export const DEFAULT_PHI_POLICY = {
  phiPolicyVersion: PHI_POLICY_VERSION,

  // Pilot mode settings
  pilotModeEnabled: true,           // Enable pilot mode banner + safety UX

  // Textarea policy
  textAreaPolicy: 'allow_warn',     // 'allow_warn' | 'block_on_detection'

  // Runtime safety requirements
  attestationRequired: true,        // Require user confirmation before submit
  scanEnabled: true,                // Enable PHI scanning on textarea fields

  // Field type enforcement
  allowedFieldTypes: ALLOWED_FIELD_TYPES,
  blockedFieldTypes: BLOCKED_FIELD_TYPES,
};

/**
 * buildPhiPolicy - Build a PHI policy document (BLOCK 13 - Pilot Mode)
 *
 * NEW SPEC: Pilot mode with runtime safety (textarea allowed with scanning + attestation)
 *
 * @param {Object} params - Policy parameters
 * @param {string} params.tenantId - Tenant ID (required)
 * @param {string} params.updatedByUid - User who updated policy (required)
 * @param {boolean} [params.pilotModeEnabled=true] - Enable pilot mode
 * @param {string} [params.textAreaPolicy='allow_warn'] - 'allow_warn' | 'block_on_detection'
 * @param {boolean} [params.attestationRequired=true] - Require attestation checkbox
 * @param {boolean} [params.scanEnabled=true] - Enable PHI scanning
 * @param {string[]} [params.allowedFieldTypes] - Override allowed field types
 * @param {string[]} [params.blockedFieldTypes] - Override blocked field types
 * @returns {Object} PHI policy document
 */
export const buildPhiPolicy = ({
  tenantId,
  updatedByUid,
  pilotModeEnabled = true,
  textAreaPolicy = 'allow_warn',
  attestationRequired = true,
  scanEnabled = true,
  allowedFieldTypes = null,
  blockedFieldTypes = null,
}) => {
  if (!tenantId) throw new Error('buildPhiPolicy: tenantId is required');
  if (!updatedByUid) throw new Error('buildPhiPolicy: updatedByUid is required');

  // Validate textAreaPolicy
  if (textAreaPolicy && !['allow_warn', 'block_on_detection'].includes(textAreaPolicy)) {
    throw new Error('buildPhiPolicy: textAreaPolicy must be "allow_warn" or "block_on_detection"');
  }

  const now = toTimestamp();

  return {
    tenantId,
    updatedAt: now,
    updatedByUid,
    phiPolicyVersion: PHI_POLICY_VERSION,

    // Pilot mode settings
    pilotModeEnabled: pilotModeEnabled !== false,

    // Textarea policy
    textAreaPolicy: textAreaPolicy || 'allow_warn',

    // Runtime safety requirements
    attestationRequired: attestationRequired !== false,
    scanEnabled: scanEnabled !== false,

    // Field type enforcement
    allowedFieldTypes: allowedFieldTypes || ALLOWED_FIELD_TYPES,
    blockedFieldTypes: blockedFieldTypes || BLOCKED_FIELD_TYPES,
  };
};

/**
 * validatePhiPolicy - Validate a PHI policy document (BLOCK 13 - Pilot Mode)
 *
 * NEW SPEC: Validates pilot mode policy structure
 *
 * @param {Object} doc - PHI policy document to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validatePhiPolicy = (doc) => {
  const errors = [];

  if (!doc.tenantId) errors.push('tenantId is required');
  if (!doc.phiPolicyVersion) errors.push('phiPolicyVersion is required');
  if (!doc.updatedByUid) errors.push('updatedByUid is required');

  // Pilot mode settings
  if (typeof doc.pilotModeEnabled !== 'boolean') {
    errors.push('pilotModeEnabled must be a boolean');
  }

  // Textarea policy
  if (!doc.textAreaPolicy) {
    errors.push('textAreaPolicy is required');
  } else if (!['allow_warn', 'block_on_detection'].includes(doc.textAreaPolicy)) {
    errors.push('textAreaPolicy must be "allow_warn" or "block_on_detection"');
  }

  // Runtime safety requirements
  if (typeof doc.attestationRequired !== 'boolean') {
    errors.push('attestationRequired must be a boolean');
  }

  if (typeof doc.scanEnabled !== 'boolean') {
    errors.push('scanEnabled must be a boolean');
  }

  // Field type enforcement
  if (!Array.isArray(doc.allowedFieldTypes)) {
    errors.push('allowedFieldTypes must be an array');
  }

  if (!Array.isArray(doc.blockedFieldTypes)) {
    errors.push('blockedFieldTypes must be an array');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * isFieldTypeSafe - Check if a field type is PHI-safe
 *
 * @param {string} fieldType - Field type to check
 * @param {Object} phiPolicy - PHI policy (or null for default)
 * @returns {boolean} True if field type is safe
 */
export const isFieldTypeSafe = (fieldType, phiPolicy = null) => {
  const policy = phiPolicy || DEFAULT_PHI_POLICY;

  // If enforcement is disabled, allow all types
  if (policy.enforcement?.enabled === false) {
    return true;
  }

  // Check if field type is in blocked list
  if (policy.blockedFieldTypes?.includes(fieldType)) {
    return false;
  }

  // Check if field type is in allowed list
  if (policy.allowedFieldTypes?.includes(fieldType)) {
    return true;
  }

  // Default: block unknown field types
  return false;
};

/**
 * validatePackPhiSafety - Validate that a pack complies with PHI policy
 *
 * @param {Object} pack - Pack to validate
 * @param {Object} phiPolicy - PHI policy (or null for default)
 * @returns {Object} { valid: boolean, errors: string[], warnings: string[] }
 */
export const validatePackPhiSafety = (pack, phiPolicy = null) => {
  const policy = phiPolicy || DEFAULT_PHI_POLICY;
  const errors = [];
  const warnings = [];

  // If enforcement is disabled, skip validation
  if (policy.enforcement?.enabled === false) {
    warnings.push('PHI enforcement is disabled');
    return { valid: true, errors, warnings };
  }

  // Validate each field
  if (pack.fields && Array.isArray(pack.fields)) {
    pack.fields.forEach((field, index) => {
      if (!field.type) {
        errors.push(`Field ${index} (${field.id || 'unknown'}): type is required`);
        return;
      }

      // Check if field type is blocked
      if (policy.blockedFieldTypes?.includes(field.type)) {
        errors.push(
          `Field "${field.id || index}" uses blocked type "${field.type}" which risks PHI capture. ` +
          `Blocked types: ${policy.blockedFieldTypes.join(', ')}`
        );
      }

      // Check if field type is allowed
      if (!isFieldTypeSafe(field.type, policy)) {
        errors.push(
          `Field "${field.id || index}" uses type "${field.type}" which is not in allowed types. ` +
          `Allowed types: ${policy.allowedFieldTypes?.join(', ')}`
        );
      }

      // Warning for text fields if allowed but risky
      if (field.type === 'text' && policy.enforcement?.allowTextFields === true) {
        warnings.push(
          `Field "${field.id || index}" is text type - ensure max length and validation prevent PHI`
        );
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * sanitizeAnswersForStorage - Remove any PHI-risky data from answers before storage
 *
 * @param {Object} answers - Raw answers object
 * @param {Object} pack - Pack schema
 * @param {Object} phiPolicy - PHI policy (or null for default)
 * @returns {Object} Sanitized answers (safe to store)
 */
export const sanitizeAnswersForStorage = (answers, pack = null, phiPolicy = null) => {
  const policy = phiPolicy || DEFAULT_PHI_POLICY;
  const sanitized = {};

  if (!answers || typeof answers !== 'object') {
    return sanitized;
  }

  // Optional pack: when provided, also drop unsafe-type fields and truncate text.
  // When absent (the Gate submit path does not hold the pack), keep every field
  // but still redact PHI VALUES from strings — strictly safer than a type-based
  // passthrough, which would store `textarea` free-text as-is (the G1 leak).
  const hasPackFields = Boolean(pack && Array.isArray(pack.fields));
  const fieldTypeMap = {};
  if (hasPackFields) {
    pack.fields.forEach((field) => {
      if (field.id) fieldTypeMap[field.id] = field.type;
    });
  }

  Object.entries(answers).forEach(([fieldId, value]) => {
    if (hasPackFields) {
      const fieldType = fieldTypeMap[fieldId];

      // If field type unknown or not safe, drop it entirely.
      if (!fieldType || !isFieldTypeSafe(fieldType, policy)) {
        console.warn(`[sanitizeAnswersForStorage] Skipping field ${fieldId} with unsafe type ${fieldType}`);
        return;
      }

      // For short text fields, truncate first, then redact PHI values.
      if (fieldType === 'text' && typeof value === 'string') {
        const maxLength = policy.enforcement?.maxTextLength || 100;
        sanitized[fieldId] = redactBlockingPhiDeep(value.substring(0, maxLength));
        return;
      }
    }

    // Value-level PHI redaction for every retained answer (textarea, select,
    // multiselect arrays, number, etc.). Structured/enum values never match PHI
    // patterns, so this only affects genuine free-text identifiers.
    sanitized[fieldId] = redactBlockingPhiDeep(value);
  });

  return sanitized;
};

// ── Fail-fast storage-safety assertion (S1 — Never-PHI write hardening) ───────

/**
 * PhiStorageViolation — typed, value-free error thrown when sanitized answers
 * would still carry a high-confidence PHI value into a Never-PHI sink.
 */
export class PhiStorageViolation extends Error {
  constructor(context) {
    super(`PHI_STORAGE_VIOLATION: high-confidence PHI value remained in ${context || 'answers'} after sanitization`);
    this.name = 'PhiStorageViolation';
    this.code = 'PHI_STORAGE_VIOLATION'; // typed code — message never includes the offending value
    this.context = context || 'answers';
  }
}

/**
 * assertAnswersStorageSafe - Fail fast if sanitized answers still contain a
 * high-confidence PHI VALUE (email / phone / long number / date). In normal
 * operation sanitizeAnswersForStorage removes all such values, so this never
 * fires; it is a safety net that turns a silent unsafe write into a loud, typed
 * failure BEFORE any Firestore write (never silently write unsafe state).
 *
 * @param {Object} sanitizedAnswers - output of sanitizeAnswersForStorage
 * @param {string} [context='referral.answers']
 * @returns {boolean} true if safe
 * @throws {PhiStorageViolation} if a high-confidence PHI value remains
 */
export const assertAnswersStorageSafe = (sanitizedAnswers, context = 'referral.answers') => {
  if (containsBlockingValuePhiDeep(sanitizedAnswers)) {
    throw new PhiStorageViolation(context);
  }
  return true;
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // ID generators
  generateReferralId,
  generateMetricId,
  generateRunId,
  generateRunSessionId,
  generateDateId,
  generateReceiptToken,

  // Timestamp helpers
  toTimestamp,
  fromTimestamp,

  // Outcome status
  OutcomeStatus,
  normalizeOutcomeStatus,

  // Override reasons
  OVERRIDE_REASONS,
  OVERRIDE_REASON_LABELS,
  OVERRIDE_NOTE_MAX_LENGTH,
  validateOverrideReason,
  buildOverrideRecord,

  // Pack normalization
  normalizePackDoc,
  validateEnginePack,

  // Document builders
  buildReferralDoc,
  buildMetricEvent,
  buildAuditLog,
  buildImpactSettings,
  buildSubmissionLock,
  buildRunSession,
  buildDailyRollup,
  buildDailyRollupIncrements,
  buildPhiPolicy,

  // Validators
  validateReferralDoc,
  validateMetricEvent,
  validateAuditLog,
  validateImpactSettings,
  validateSubmissionLock,
  validateRunSession,
  validateDailyRollup,
  validatePhiPolicy,
  validatePackPhiSafety,

  // PHI Safety Helpers
  isFieldTypeSafe,
  sanitizeAnswersForStorage,
  assertAnswersStorageSafe,
  PhiStorageViolation,

  // Constants
  METRIC_ALLOWED_FIELDS,
  DEFAULT_IMPACT_SETTINGS,
  ALLOWED_FIELD_TYPES,
  BLOCKED_FIELD_TYPES,
  PHI_POLICY_VERSION,
  DEFAULT_PHI_POLICY,
};
